import { Link } from "react-router-dom";
import { useT } from "@/lib/i18n";
import BridgeModelLogo from "@/components/site/BridgeModelLogo";
import SamGovBadge from "@/components/site/SamGovBadge";
import { parentBrand, activeDeployment, expansionRoadmap } from "@/lib/deployments";

export default function Footer() {
  const { t } = useT();
  return (
    <footer data-testid="site-footer" className="bg-[#0A192F] text-[#F9F7F1] pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 pb-12 border-b border-[#F9F7F1]/15">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4 mb-5">
              <BridgeModelLogo
                size={56}
                primary="#F9F7F1"
                accent="#D4AF37"
                sunGlow="#F4D77A"
                data-testid="footer-logo-mark"
              />
              <div>
                <div className="font-display text-xl leading-none">
                  The <em className="italic">Bridge</em> Model
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mt-1.5">
                  {parentBrand.domain}
                </div>
              </div>
            </div>
            <p className="text-[#CBD5E1] leading-relaxed max-w-md">{parentBrand.description}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] mt-4">
              Currently reviewing · Proposal 0{activeDeployment.deploymentNumber} · {activeDeployment.city}, {activeDeployment.state} · {activeDeployment.statusShort}
            </p>
            <div className="mt-4">
              <SamGovBadge data-testid="footer-sam-badge" />
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-4">
              {t("footer.sections")}
            </div>
            <ul className="space-y-2 text-sm text-[#CBD5E1]">
              <li><a href="#context" className="hover:text-[#F9F7F1]">{t("nav.localContext")}</a></li>
              <li><a href="#strategy" className="hover:text-[#F9F7F1]">{t("nav.strategy")}</a></li>
              <li><a href="#trades" className="hover:text-[#F9F7F1]">{t("nav.trades")}</a></li>
              <li><a href="#seniors" className="hover:text-[#F9F7F1]">{t("nav.seniors")}</a></li>
              <li><a href="#framework" className="hover:text-[#F9F7F1]">{t("nav.framework")}</a></li>
              <li><a href="#network" className="hover:text-[#F9F7F1]">{t("nav.network")}</a></li>
              <li><a href="#budget" className="hover:text-[#F9F7F1]">{t("nav.budget")}</a></li>
              <li><a href="#implementation" className="hover:text-[#F9F7F1]">Implementation</a></li>
              <li><a href="#partnership" className="hover:text-[#F9F7F1]">{t("nav.cdbg")}</a></li>
              <li className="pt-3 border-t border-[#F9F7F1]/15">
                <Link to="/pitch" data-testid="footer-pitch-link" className="hover:text-[#D4AF37]">{t("nav.pitch")} →</Link>
              </li>
              <li>
                <Link to="/press" data-testid="footer-press-link" className="hover:text-[#D4AF37]">{t("nav.press")} →</Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-4">
              {t("footer.contact")}
            </div>
            <p className="font-display text-lg">{parentBrand.proprietor}</p>
            <p className="text-sm text-[#CBD5E1] mt-1">
              Proprietor &amp; Program Designer · {parentBrand.consultancy}<br />
              Licensor of The Bridge Model methodology
            </p>
            <a
              href={`mailto:${parentBrand.contactEmail}`}
              data-testid="footer-contact-email"
              className="inline-block mt-4 font-mono text-xs uppercase tracking-widest text-[#D4AF37] hover:text-[#F9F7F1]"
            >
              {parentBrand.contactEmail}
            </a>

            {/* Expansion roadmap — signals to visiting cities this is a portfolio, not a one-off */}
            <div className="mt-6 pt-5 border-t border-[#F9F7F1]/15">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-3">
                Deployment Roadmap
              </div>
              <ul data-testid="footer-roadmap" className="space-y-1.5 text-[11px] text-[#CBD5E1]">
                {expansionRoadmap.map((r) => (
                  <li key={r.city} className="flex items-baseline justify-between gap-3">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-[#94A3B8] shrink-0">
                      {r.period}
                    </span>
                    <span className="text-right">
                      <span className={r.status === "active" ? "text-[#D4AF37] font-semibold" : ""}>
                        {r.city}
                      </span>
                      <span className="text-[#64748B] block text-[10px]">{r.phase}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="pt-8 space-y-3">
          <p
            data-testid="footer-equity-line"
            className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] leading-relaxed"
          >
            The Bridge Model serves underserved households of every ethnic and
            social background &mdash; without prejudice, without precondition.
            Title VI &middot; Fair Housing &middot; ADA &middot; Section 504 compliant.
          </p>
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 pt-4 border-t border-[#F9F7F1]/10">
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] leading-relaxed max-w-3xl">
              © {new Date().getFullYear()} {parentBrand.consultancy} · All rights reserved · The Bridge Model methodology (Hub-and-Spokes architecture, Service Connector caseload structure, three-portfolio framework, train-the-trainer curriculum, intake matrices, financial-forecast templates, and dossier content) is the proprietary work product of {parentBrand.proprietor}, protected under U.S. copyright law, the Texas Uniform Trade Secrets Act, and the federal Defend Trade Secrets Act. Unauthorized reproduction, distribution, or replication is strictly prohibited.
            </p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#64748B] max-w-xl md:text-right leading-relaxed">
              Proposal access governed by a click-wrap reviewer acknowledgment. All acknowledgments are logged for audit. Document status: <span className="text-[#D4AF37]">Proposed · Pending Approval · Not Yet Adopted</span>.<br className="hidden md:block" />{t("footer.dataNote")}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

